import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Twitter, Linkedin } from "lucide-react";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { api } from "@/lib/api";

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
};

export default function Footer() {
  const { settings } = useSiteSettings();
  const social = settings?.social || {};
  const contact = settings?.contact || {};
  const [footerPages, setFooterPages] = useState([]);

  useEffect(() => {
    api.get("/pages").then((r) => {
      setFooterPages((r.data.pages || []).filter((p) => p.show_in_footer));
    }).catch(() => setFooterPages([]));
  }, []);

  return (
    <footer className="bg-[#0A192F] text-slate-300 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Way2Efiling" className="h-10 object-contain" data-testid="footer-logo-img" />
            ) : (
              <>
                <span className="inline-flex w-9 h-9 rounded-md bg-white/10 items-center justify-center text-white font-bold font-display">W2</span>
                <div className="font-display font-bold text-white text-lg">Way2Efiling</div>
              </>
            )}
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            A service platform operated by <span className="text-white font-medium">SHREE ADARSH INFOTECH</span>. Trusted tax & license filing for individuals and businesses across India.
          </p>
          <div className="mt-5 flex gap-2" data-testid="footer-social">
            {Object.keys(SOCIAL_ICONS).map((key) => {
              const url = social[key];
              if (!url) return null;
              const Icon = SOCIAL_ICONS[key];
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                  aria-label={key}
                  data-testid={`footer-social-${key}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <div className="section-label text-slate-400 mb-4">Quick Links</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/news" className="hover:text-white">News</Link></li>
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
            <li><Link to="/track-order" className="hover:text-white">Track Order</Link></li>
            {footerPages.map((p) => (
              <li key={p.slug}><Link to={`/pages/${p.slug}`} className="hover:text-white" data-testid={`footer-page-${p.slug}`}>{p.title}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="section-label text-slate-400 mb-4">Services</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/service/itr-filing" className="hover:text-white">ITR Filing</Link></li>
            <li><Link to="/service/gst-registration" className="hover:text-white">GST Registration</Link></li>
            <li><Link to="/service/udyam-registration" className="hover:text-white">Udyam Registration</Link></li>
            <li><Link to="/service/fssai-license" className="hover:text-white">FSSAI License</Link></li>
          </ul>
        </div>

        <div>
          <div className="section-label text-slate-400 mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0" /><a href={`tel:${contact.phone || "8448440219"}`} className="hover:text-white">{contact.phone || "8448440219"}</a></li>
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /><a href={`mailto:${contact.email || "support@way2efiling.com"}`} className="hover:text-white break-all">{contact.email || "support@way2efiling.com"}</a></li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Q1 &amp; Q2, Jain Building, Main Market, Sandiya, Sojat, Pali, Rajasthan – 306306</span>
            </li>
            <li className="pt-2 text-xs text-slate-400">GSTIN: <span className="font-mono text-slate-300">08KKOPS0455P1Z2</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-400 flex flex-col md:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} SHREE ADARSH INFOTECH. All rights reserved.</div>
          <div>Way2Efiling – Tax &amp; License Filing Simplified</div>
        </div>
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 -mt-1 text-[11px] leading-relaxed text-slate-500"
          data-testid="footer-disclaimer"
        >
          <span className="font-semibold text-slate-400">Disclaimer:</span> Way2eFiling is a privately operated facilitation platform and is not affiliated with, endorsed by, or connected to any government authority. We assist customers in accessing registration, filing and professional services through independent professionals and may engage third-party service providers where required. We are not a law firm and do not provide legal advice or legal representation. Website content is for general informational purposes only. Fees charged are for facilitation/platform services, and all government registrations, approvals and certificates remain subject to the decision of the concerned authority.
        </div>
      </div>
    </footer>
  );
}
